import { noop } from 'lodash';
import { type Dispatch, type ReactNode, type SetStateAction, createContext } from 'react';

import type RecentMembers from '@/lib/Creators/Dashboard/Dashboard/RecentMembers';
import { type getCreatorProducts, type getRoutableCompany } from '@/tsClient';
import type getCreatorProfile from '@/tsClient/creators/getCreatorProfile';
import type getRecentlyUsedPrices from '@/tsClient/creators/getRecentlyUsedPrices';
import type getCreatorCollections from '@/tsClient/getCreatorCollections';
import type * as ApiModels from '@/typings/api-models';

type RoutableCompany = Awaited<ReturnType<typeof getRoutableCompany>>;
type Creator = Awaited<ReturnType<typeof getCreatorProfile>>;
type Collections = Awaited<ReturnType<typeof getCreatorCollections>>[0]['productTypes'];
type Product = Awaited<ReturnType<typeof getCreatorProducts>>['products'][0];
type Price = Awaited<ReturnType<typeof getRecentlyUsedPrices>>[0];

type DashboardContextState = {
  backLink: string | null;
  backFallbackLink: string | null;
  fetchPrices: () => Promise<unknown>;
  fetchProducts: () => Promise<unknown>;
  fetchCollections: () => Promise<unknown>;
  creator: Creator | null;
  customStatuses: ApiModels.CustomStatus[] | null;
  collections: Collections | null;
  prices: Price[] | null;
  products: Product[] | null;
  setTitle: (title: string | null) => void;
  isInDashboard: boolean;
  isPaymentModalOpen: boolean;
  isTeamsModalOpen: boolean;
  isUserModalOpen: boolean;
  isShareProductModalOpen: string;
  isCreateServiceModalOpen: boolean;
  isInitiateBookingModalOpen: boolean;
  newChatMessageIds: Array<string>;
  setNewChatMessageIds: Dispatch<SetStateAction<Array<string>>>;
  setLastViewedGladeConversation: Dispatch<SetStateAction<{ conversationSid: string; timestamp: string } | null>>;
  gladeConversationSid: string | null;
  triggerRefetchBooking: () => void;
  recentMembers: Parameters<typeof RecentMembers>[0]['data'];
  refetchBookingsRefreshKey: number;
  refetchMembersKey: number;
  triggerRefetchMembers: () => void;
  topContent: ReactNode;
  title: string | null;
  setIsTFAModalOpen: (isOpen: boolean) => void;
  setAppModalOpen: (isOpen: boolean) => void;
  setBrowserTabTitle: (title: string | null) => void;
  setModalOpen: (isOpen: boolean) => void;
  setPaymentModalOpen: (isOpen: boolean) => void;
  setTeamsModalOpen: (isOpen: boolean) => void;
  setIsUserModalOpen: (isOpen: boolean) => void;
  setRecentMembers: (members: Parameters<typeof RecentMembers>[0]['data']) => void;
  setTopContent: (content: ReactNode) => void;
  setTitleContent: (content: ReactNode) => void;
  setBackLink: (link: string | null) => void;
  setBackFallbackLink: (link: string | null) => void;
  routableCompany: RoutableCompany | null;
  refetchCreator: () => Promise<Creator | null>;
  refetchUnreadInboxItemCount: () => Promise<void>;
  setKeyboardShortcutsModalOpen: (isOpen: boolean) => void;
  setNotificationModalOpen: (isOpen: boolean) => void;
  setWidgetSnippetModalOpen: (isOpen: boolean) => void;
  setDocumentRequestsPasscodeModalOpen: (isOpen: boolean) => void;
  setIsCreateServiceModalOpen: (isOpen: boolean) => void;
  setIsShareProductModalOpen: (productSlug: string) => void;
  setInitiateBookingModalOpen: (isOpen: boolean) => void;
  setCustomerSupportChatModalOpen: (isOpen: boolean) => void;
};

const DashboardContext = createContext<DashboardContextState>({
  backFallbackLink: null,
  backLink: null,
  collections: null,
  creator: null,
  customStatuses: [],
  fetchCollections: async () => {
    /* empty */
  },
  fetchPrices: async () => {
    /* empty */
  },
  fetchProducts: async () => {
    /* empty */
  },
  gladeConversationSid: null,
  isCreateServiceModalOpen: false,
  isInDashboard: false,
  isInitiateBookingModalOpen: false,
  isPaymentModalOpen: false,
  isShareProductModalOpen: '',
  isTeamsModalOpen: false,
  isUserModalOpen: false,
  newChatMessageIds: [],
  prices: null,
  products: null,
  recentMembers: [],
  refetchBookingsRefreshKey: 0,
  refetchCreator: async () => null,
  refetchMembersKey: 0,
  refetchUnreadInboxItemCount: async () => {
    /* empty */
  },
  routableCompany: null,
  setAppModalOpen: () => {
    /* empty */
  },
  setBackFallbackLink: () => {
    /* empty */
  },
  setBackLink: () => {
    /* empty */
  },
  setBrowserTabTitle: () => {
    /* empty */
  },
  setCustomerSupportChatModalOpen: () => {
    /* empty */
  },
  setDocumentRequestsPasscodeModalOpen: () => {
    /* empty */
  },
  setInitiateBookingModalOpen: () => {
    /* empty */
  },
  setIsCreateServiceModalOpen: () => {
    /* empty */
  },
  setIsShareProductModalOpen: () => {
    /* empty */
  },
  setIsTFAModalOpen: () => {
    /* empty */
  },
  setIsUserModalOpen: () => {
    /* empty */
  },
  setKeyboardShortcutsModalOpen: () => {
    /* empty */
  },
  setLastViewedGladeConversation: () => {
    /* empty */
  },
  setModalOpen: () => {
    /* empty */
  },
  setNewChatMessageIds: () => {
    /* empty */
  },
  setNotificationModalOpen: () => {
    /* empty */
  },
  setPaymentModalOpen: () => {
    /* empty */
  },
  setRecentMembers: () => {
    /* empty */
  },
  setTeamsModalOpen: () => {
    /* empty */
  },
  setTitle: () => {
    /* empty */
  },
  setTitleContent: () => {
    /* empty */
  },
  setTopContent: () => {
    /* empty */
  },
  setWidgetSnippetModalOpen: () => {
    /* empty */
  },
  title: null,
  topContent: null,
  triggerRefetchBooking: noop,
  triggerRefetchMembers: noop,
});

export default DashboardContext;
